import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CreateUserGQL,
  CreateUserInput,
  CurrentUserGQL,
  CurrentUserQuery,
  SettingGql,
  UpdateUserGQL,
  UpdateUserInput,
  UserGql,
  UserGQL,
  UserQuery
} from '@app/graphql/generated';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, map, tap } from 'rxjs/operators';
import { combineLatest, iif, Observable, of, timer } from 'rxjs';
import { ApolloQueryResult } from 'apollo-client';
import gql from 'graphql-tag';
import { MatSnackBar } from '@angular/material/snack-bar';
import { config } from '@app/app.config';
import { EngineKeyToNameMap, EngineList } from '@app/shared/engine-list';
import { UserDTO, UsersService } from '@app/services/users.service';

interface UserFormModel {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  permissions: Record<string, boolean>;
  role: string;
  retainAudio: boolean;
  audioTtl: number;
  fullScreen: boolean;
  minDisplayTime: number;
  eventExpiration: number;
}

export enum SettingKey {
  store_audio = 'store_audio',
  audio_ttl = 'audio_ttl',
  full_screen = 'full_screen',
  min_display_time = 'min_display_time',
  event_expiration = 'event_expiration'
}

type CurrentUserModel = ApolloQueryResult<CurrentUserQuery>['data']['currentUser'];
type UserModel = UserQuery['user'];

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss']
})
export class EditUserComponent implements OnInit {
  @Input() isNew: boolean;

  public currentUser;
  public existingUser: UserModel;

  public userRolesDropdown = [
    { value: 'admin', label: 'Admin' },
    { value: 'business', label: 'Business' },
    { value: 'user', label: 'User' }
  ];

  public permissions = [];

  public permissionsOptions: any = [];

  form: FormGroup;
  showUserSaved: boolean;

  userRole;
  retainAudio;
  audioRetentionTime;
  fullScreen;
  minDisplayTime;
  eventExpiration;
  maxEncoderDisplayTime;
  defaultEncoderDisplayTime;
  ownerId;

  
  users$: Observable<{ value: number; label: string }[]>;

  constructor(
    private createUserGQL: CreateUserGQL,
    private updateUserGQL: UpdateUserGQL,
    private route: ActivatedRoute,
    private userGql: UserGQL,
    private router: Router,
    private currentUserGQL: CurrentUserGQL,
    private userService: UsersService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar) {
      this.maxEncoderDisplayTime = config.maxEncoderLineTime;
      this.defaultEncoderDisplayTime = config.defaultEncoderLineTime;
  }

  get formValue() {
    return this.form.value as UserFormModel;
  }

  get permissionsFormGroup() {
    return this.form.get('permissions') as FormArray;
  }

  setupForm(existingUser) {
    const fb = this.fb;
    const defaultValues = { ...existingUser };

    this.retainAudio = Boolean(defaultValues.settings.find(s => s.key === SettingKey.store_audio)?.value === 'true') || false;
    this.audioRetentionTime = defaultValues.settings.find(s => s.key === SettingKey.audio_ttl)?.value || '30';
    this.fullScreen = Boolean(defaultValues.settings.find(s => s.key === SettingKey.full_screen)?.value === 'true') || true;
    this.minDisplayTime = Number(defaultValues.settings.find(s => s.key === SettingKey.min_display_time)?.value || this.defaultEncoderDisplayTime);
    this.eventExpiration = defaultValues.settings.find(s => s.key === SettingKey.event_expiration)?.value || '30';

    // Form
    const form = this.fb.group({
      firstName: fb.control(defaultValues.firstName),
      lastName: fb.control(defaultValues.lastName),
      email: fb.control(defaultValues.email),
      username: fb.control(defaultValues.username),
      password: fb.control(null, [
        Validators.pattern(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&]).{8,100}$/
        )
      ]),
      confirmPassword: fb.control(null),
      permissions: fb.array([]),
      role: fb.control(defaultValues.role),
      retainAudio: fb.control(this.retainAudio),
      audioTtl: fb.control(this.audioRetentionTime),
      fullScreen: fb.control(this.fullScreen),
      minDisplayTime: fb.control(this.minDisplayTime),
      eventExpiration: fb.control(this.eventExpiration),
      ownerId: fb.control(this.ownerId)
    });

    this.userRole = defaultValues.role;
    
    form.valueChanges.subscribe(val => console.log(val));
    this.form = form;
  }

  ngOnInit() {
    this.users$ = this.userService.getUsers().pipe(
      map(users =>
        users
          .filter(user => user.role === 'business')
          .map(user => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName} (${user.email})`
          }))
      ),
      tap(() => {
        if (this.existingUser?.ownerId) {
          this.form?.get('ownerId')?.patchValue(this.existingUser.ownerId);
        }
      })
    );

    const currentUser$ = this.currentUserGQL.fetch().pipe();
    const existingUser$ = this.existingUser$().pipe(
      tap(data => this.setupForm(data.data.user))
    );

    const viewModel = combineLatest([currentUser$, existingUser$])
      .pipe(
        map(([currentUserRes, existingUserRes]) => {
          const returnValue = {
            currentUser: currentUserRes.data.currentUser,
            existingUser:
              existingUserRes &&
              existingUserRes.data &&
              existingUserRes.data.user
          };
          return returnValue;
        }),
        tap(({ currentUser, existingUser }) => {
          this.updateAvailablePermissions(existingUser, currentUser);
          this.currentUser = currentUser;
          this.existingUser = existingUser;
        })
      )
      .subscribe();
  }

  submit() {
    this.isNew ? this.createUser() : this.updateUser();
  }

  createUser() {
    const user: any = this.userFormToCreateUserModel(this.form.value);
    console.log(user);
    this.createUserGQL.mutate({ user }).subscribe((res) => {
      const { errors } = res;

      if (errors) {
        const message = errors[0].message;

        switch (message) {
          case 'Email already taken':
            this.snackBar.open('This email has already been taken', 'Dismiss', { duration: 3500 });
            break;
          case 'Username already taken':
            this.snackBar.open('This username has already been taken', 'Dismiss', { duration: 3500 });
            break;
        }
      } else {

        this.snackBar.open('User created successfully!', 'Dismiss', { duration: 3500 });
        this.router.navigate(['/account/users']);
      }
    });


  }

  updateAvailablePermissions(
    existingUser: UserModel,
    currentUser: CurrentUserModel
  ) {
    const permissions = currentUser.permissions.map(permission => {
      return { name: EngineKeyToNameMap[permission.name] || permission.name, value: permission.uuid };
    });

    this.permissions = permissions;

    // Permissions group
    const permissionsFormGroup = this.form.controls.permissions as FormArray;

    // Dict of permission UUIDs belonging to the user we're editing
    const existingUserPermissionsByUuid = {};

    if (existingUser) {
      existingUser.permissions.map(
        permission => (existingUserPermissionsByUuid[permission.uuid] = true)
      );
    }

    // For each of OUR permissions add a form control
    permissionsFormGroup.clear();
    permissions.map(permission => {
      permissionsFormGroup.push(
        this.fb.control(existingUserPermissionsByUuid[permission.value])
      );
    });
  }

  userFormToCreateUserModel(userFormValue: UserFormModel): CreateUserInput {
    const permissions = this.permissions
      .map((permission, i) => {
        return this.form.value.permissions[i] ? permission.value : null;
      })
      .filter(Boolean);

    delete userFormValue.confirmPassword;

    const user: CreateUserInput = {
      ...userFormValue,
      minDisplayTime: this.minDisplayTime,
      permissions
    };

    return user;
  }

  userFormToUpdateUserModel(userFormValue: UserFormModel): CreateUserInput {
    const permissions = this.permissions
      .map((permission, i) => {
        return this.form.value.permissions[i] ? permission.value : null;
      })
      .filter(Boolean);

    delete userFormValue.confirmPassword;

    if (!userFormValue.password) {
      delete userFormValue.password;
    }

    const user: CreateUserInput = {
      ...userFormValue,
      shortId: this.existingUser.shortId,
      minDisplayTime: this.minDisplayTime,
      permissions
    };

    return { ...user };
  }

  updateUser() {
    const user = this.userFormToUpdateUserModel(this.formValue);
    console.log(user);
    this.updateUserGQL.mutate({ user }).subscribe(() => {
      this.snackBar.open('User saved successfully!', 'Dismiss', { duration: 3500 });
      this.router.navigate(['/account/users']);

    });
  }

  private existingUser$(): Observable<ApolloQueryResult<UserQuery> | null> {
    if (this.isNew) {
      // @ts-ignore
      return of({ data: { user: { permissions: [], settings: [] } } });
    }

    const userId = this.route.snapshot.params.userId;

    return this.userGql.fetch({ shortId: userId }, { fetchPolicy: 'network-only' });
  }

  formatSliderLabel(value: number): string {
    return `${value}`;
  }
}
